import { useState, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import type { FieldErrorLike } from '@/types/form';
import { getCity, getCityBySearch } from '@/lib/helpers';

import Text from '@/components/forms/text';

interface CityOption {
    ville: string;
    codePostal: string;
}

export interface PostalCodeCityProps {
    /** Nom du champ code postal dans le formulaire. */
    cp: string;
    /** Nom du champ ville dans le formulaire. */
    ville: string;
    required?: boolean;
    label?: ReactNode;
    error?: FieldErrorLike;
    defaultValue?: string;
    disabled?: boolean;
}

/** Recherche d'une commune par code postal (5 chiffres) ou par nom (3 lettres), qui remplit code postal et ville. */
const PostalCodeCity = (props: PostalCodeCityProps) => {
    const { cp, ville, required, label, error, defaultValue, disabled } = props;
    const { setValue, getValues } = useFormContext();
    const values = getValues() as Record<string, string | undefined>;
    const [city, setCity] = useState<CityOption[]>([]);
    const [selected, setSelected] = useState(false);
    const [textValue, setTextValue] = useState(values[cp] && values[ville] ? `${values[cp]} ${values[ville]}` : '');

    async function fetchCityByPostalCode(value: string) {
        const cities = await getCity<string[]>(value);
        setCity((cities ?? []).map((v) => ({ ville: v, codePostal: value })));
    }

    async function fetchCityBySearch(value: string) {
        const cities = await getCityBySearch<CityOption[]>(value);
        setCity(cities);
    }

    const searchCity = (value: string) => {
        setSelected(false);
        if (parseInt(value, 10)) {
            if (value.length === 5) void fetchCityByPostalCode(value);
        } else if (value.length >= 3) {
            void fetchCityBySearch(value);
        }
    };

    const setCityAndPostalCode = (cityClicked: CityOption) => {
        setSelected(true);
        setValue(cp, cityClicked.codePostal, { shouldValidate: true });
        setValue(ville, cityClicked.ville, { shouldValidate: true });
        setTextValue(`${cityClicked.codePostal} ${cityClicked.ville}`);
    };

    const isOpen = !selected && city.length > 0;

    return (
        <div className="relative">
            <Text
                type="text"
                required={required}
                label={label}
                onChange={(e) => {
                    setTextValue(e.target.value);
                    searchCity(e.target.value);
                }}
                onBlur={(e) => e.target.value === '' && setSelected(true)}
                value={textValue || defaultValue}
                error={error}
                disabled={disabled}
                role="combobox"
                aria-expanded={isOpen}
                aria-autocomplete="list"
                aria-haspopup="listbox"
            />
            {isOpen && (
                <div
                    className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden"
                    role="listbox"
                    aria-label={typeof label === 'string' ? label : undefined}
                >
                    {city.map((c, index) => (
                        <button
                            type="button"
                            role="option"
                            key={index}
                            onClick={() => setCityAndPostalCode(c)}
                            className="w-full px-6 py-2.5 text-left text-sm cursor-pointer hover:bg-primary/5 transition-colors border-b border-border last:border-b-0"
                        >
                            <span className="font-medium">{c.ville}</span> <span className="text-muted-foreground">{c.codePostal}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PostalCodeCity;
